import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, CircularProgress, useMediaQuery, useTheme,
} from '@mui/material';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts';
import evaluationsApi, { DashboardData } from '../services/evaluationService';
import { fmtMoney } from '../utils/formatMoney';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const COLORS = ['#1976d2', '#ed6c02', '#2e7d32', '#9c27b0', '#d32f2f', '#0288d1', '#7b1fa2', '#f57c00'];

interface KpiCardProps {
  title: string;
  last30: number;
  year: number;
  color: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ title, last30, year, color }) => (
  <Paper sx={{ p: 2, textAlign: 'center', borderTop: `3px solid ${color}` }}>
    <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{title}</Typography>
    <Typography variant="h3" fontWeight={700} sx={{ lineHeight: 1.1 }}>{year}</Typography>
    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
      Last 30 days: <strong>{last30}</strong>
    </Typography>
  </Paper>
);

const DashboardPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<DashboardData | null>(null);

  useEffect(() => {
    evaluationsApi.dashboard()
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (!data) return <Typography color="text.secondary" sx={{ p: 4 }}>Failed to load dashboard.</Typography>;

  const { kpis, evals_per_sales, amount_per_sales, monthly_data } = data;

  const monthlyChartData = (() => {
    const months: string[] = [];
    const now = new Date();
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(`${MONTH_NAMES[d.getMonth()]} ${d.getFullYear().toString().slice(2)}`);
    }

    const salesNames = Array.from(new Set(monthly_data.map(r => r.name)));
    const chartData = months.map((label, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
      const yr = d.getFullYear();
      const mo = d.getMonth() + 1;
      const row: Record<string, any> = { month: label };
      salesNames.forEach(name => {
        const match = monthly_data.find(r => r.name === name && r.year === yr && r.month === mo);
        row[name] = match ? match.amount : 0;
      });
      return row;
    });
    return { chartData, salesNames };
  })();

  const salesBarHeight = isMobile
    ? Math.max(150, evals_per_sales.length * 40 + 40)
    : 320;

  const amountBarHeight = isMobile
    ? Math.max(150, amount_per_sales.length * 40 + 40)
    : 320;

  const monthlyHeight = isMobile
    ? Math.max(200, monthlyChartData.chartData.filter(d => {
        return monthlyChartData.salesNames.some(n => d[n] > 0);
      }).length * 35 + 60)
    : 380;

  return (
    <Box>
      <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={600} gutterBottom>Dashboard</Typography>

      <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: isMobile ? 1 : 2, mb: isMobile ? 2 : 3 }}>
        <KpiCard title="Total Evaluations" last30={kpis.last_30d.total} year={kpis.year.total} color="#1976d2" />
        <KpiCard title="Pending" last30={kpis.last_30d.pending} year={kpis.year.pending} color="#ed6c02" />
        <KpiCard title="Approved" last30={kpis.last_30d.approved} year={kpis.year.approved} color="#2e7d32" />
        <KpiCard title="Rejected" last30={kpis.last_30d.rejected} year={kpis.year.rejected} color="#d32f2f" />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 1.5 : 2, mb: isMobile ? 1.5 : 3 }}>
        <Paper sx={{ p: isMobile ? 1.5 : 2 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontSize: isMobile ? '0.8rem' : undefined }}>Evaluations per Sales Person (YTD)</Typography>
          <ResponsiveContainer width="100%" height={salesBarHeight}>
            <BarChart data={evals_per_sales} layout="vertical" margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: isMobile ? 10 : 12 }} />
              <YAxis type="category" dataKey="name" width={isMobile ? 70 : 100} tick={{ fontSize: isMobile ? 10 : 12 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#1976d2" name="Evaluations" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>

        <Paper sx={{ p: isMobile ? 1.5 : 2 }}>
          <Typography variant="subtitle2" gutterBottom sx={{ fontSize: isMobile ? '0.8rem' : undefined }}>Amount per Sales Person (YTD)</Typography>
          <ResponsiveContainer width="100%" height={amountBarHeight}>
            <BarChart data={amount_per_sales} layout="vertical" margin={{ left: 0, right: 10, top: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: isMobile ? 10 : 12 }} />
              <YAxis type="category" dataKey="name" width={isMobile ? 70 : 100} tick={{ fontSize: isMobile ? 10 : 12 }} />
              <Tooltip formatter={(value: any) => fmtMoney(value)} />
              <Bar dataKey="amount" fill="#2e7d32" name="Amount" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Box>

      <Paper sx={{ p: isMobile ? 1.5 : 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontSize: isMobile ? '0.8rem' : undefined }}>Monthly Amount per Sales Person (Last 12 Months)</Typography>
        {isMobile ? (
          <ResponsiveContainer width="100%" height={monthlyHeight}>
            <BarChart
              data={monthlyChartData.chartData.filter(d => monthlyChartData.salesNames.some(n => d[n] > 0))}
              layout="vertical"
              margin={{ left: 0, right: 10, top: 5, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="month" width={50} tick={{ fontSize: 10 }} />
              <Tooltip formatter={(value: any) => fmtMoney(value)} />
              <Legend wrapperStyle={{ fontSize: 10 }} />
              {monthlyChartData.salesNames.map((name, i) => (
                <Bar key={name} dataKey={name} fill={COLORS[i % COLORS.length]} stackId="a" radius={[0, 4, 4, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <ResponsiveContainer width="100%" height={380}>
            <BarChart data={monthlyChartData.chartData} margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(value: any) => fmtMoney(value)} />
              <Legend />
              {monthlyChartData.salesNames.map((name, i) => (
                <Bar key={name} dataKey={name} fill={COLORS[i % COLORS.length]} stackId="a" />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </Paper>
    </Box>
  );
};

export default DashboardPage;
